import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import errorBoundary from '@ifeng-fe/errorBoundary';
import { pics } from '@ifeng-fe/three_terminal/es/mobile/pics';
import { formatImage } from '@ifeng-fe/three_terminal/es/base/publicMethod/formatImage';
import { LazyImg } from '@ifeng-fe/ui_pc_lazyLoad';
import { getStatusBarHeight, setH5PageTouchArea, setNoSlideArea } from '@src/public/utils/app';
import { EVENT_LIST_INFLOW } from '@src/public/constants';
import { dispatchPage, linkBase } from '@src/mobile/components/Link';
import { inFlowIframeJump } from '@src/public/utils';
import styles from './index.css';
import defaultImg from './defaultImg.png';

const cx = classnames.bind(styles);

const Images = ({ img, showType, id }) => {
    // 处理图片列表
    const imgList =
        img && Array.isArray(img) && img.length
            ? img.map(item => {
                  const { url } = item;

                  // 是否为动图
                  const isGif = url.includes('.gif');

                  // 裁剪图片
                  const formatUrl = isGif ? url.replace(/\.webp|_webp/g, '') : formatImage(url, 340, 340);

                  const imgReg =
                      (url &&
                          url.split('/') &&
                          url.split('/').pop() &&
                          url
                              .split('/')
                              .pop()
                              .split('.') &&
                          url
                              .split('/')
                              .pop()
                              .split('.')[0] &&
                          url
                              .split('/')
                              .pop()
                              .split('.')[0]
                              .split('_')) ||
                      [];

                  const imgWidth = (imgReg[1] && imgReg[1].replace('w', '')) || 0;
                  const imgHeight = (imgReg[2] && imgReg[2].replace('h', '')) || 0;

                  const isLong = imgWidth && imgHeight && (imgWidth / imgHeight < 0.66 || imgWidth / imgHeight > 2);

                  return {
                      url,
                      formatUrl,
                      isGif,
                      isLong,
                  };
              })
            : [];

    // 正常情况下每行显示的图片张数
    const colNumNormal = 3;
    // 图片列表长度为4时每行显示的图片张数
    const colNumSize4 = 2;
    // 实际每行显示的图片张数
    const colNum = imgList.length === 4 ? colNumSize4 : colNumNormal;

    const getRowList = imgList => {
        // 图片列表是否只有一张图片
        const isSingle = imgList.length === 1;
        // 最大展示的图片张数
        const maxShowSize = 9;
        // 超出最大展示的图片张数
        const overSize = imgList.length - maxShowSize;
        // 最后返回的图片列表分行存储数组
        const rowList = [];

        imgList.slice(0, maxShowSize).forEach((item, index) => {
            const row = Math.floor(index / colNum);

            if (isSingle) {
                item.isSingle = isSingle;
            }

            if (index === maxShowSize - 1) {
                item.overSize = overSize;
            }

            if (rowList[row] && Array.isArray(rowList[row])) {
                rowList[row].push(item);
            } else {
                rowList[row] = [item];
            }
        });

        return rowList;
    };

    const rowList = getRowList(imgList);

    // 放大图片 / 页面跳转
    const handleShowPic = (e, index) => {
        // 如果是嵌入专题热点，则使用PostMessage通知iframe外层跳转
        // 此处仅考虑端外情况，因为端内原生专题热点无法使用原生查看图片方法
        if (showType === EVENT_LIST_INFLOW && id) {
            const url = `${linkBase}/loc/timeline/event/${id}`;

            if (window.grounds) {
                dispatchPage({ to: url });
            } else {
                inFlowIframeJump(url);
            }

            return;
        }

        const list = imgList.map(item => {
            const { url } = item;
            const reg = /_w([0-9]+)_h([0-9]+)\.[a-zA-Z]+$/gi;

            if (reg.test(url)) {
                const width = parseInt(RegExp.$1);
                const height = parseInt(RegExp.$2);

                return { url, width, height };
            } else {
                return { url, width: 690, height: 990 };
            }
        });

        if (window?.grounds?.openSlide) {
            const { width, height, top, left } = e.target.getBoundingClientRect();

            window.grounds.openSlide(JSON.stringify(list.map(item => item.url)), index, {
                top,
                left,
                width,
                height,
                img:
                    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMgAAADICAYAAACtWK6eAAAAAXNSR0IArs4c6QAAAERlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAA6ABAAMAAAABAAEAAKACAAQAAAABAAAAyKADAAQAAAABAAAAyAAAAACbWz2VAAAC0UlEQVR4Ae3QgQAAAADDoPlTH+SFUGHAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgAEDBgwYMGDAgIGvgQFx5gABkajORgAAAABJRU5ErkJggg==',
            });
        } else {
            pics.show({
                list,
                current: index,
                // 配置对象不需要可以不写
                options: {
                    statusBarHeight: `${getStatusBarHeight()}px`,
                    onShow: () => {
                        setNoSlideArea([{ top: 0, end: window.innerHeight, type: 'fixed' }]);
                        setH5PageTouchArea(0, window.innerHeight, window.innerHeight);
                    },
                    onClose: () => {
                        setNoSlideArea([
                            {
                                top: window.h5PageTouchAreaTop,
                                end: window.window.h5PageTouchAreaBottom,
                                type: 'fixed',
                            },
                        ]);
                        // 还原初始值，或去掉禁止的手势
                        setH5PageTouchArea(
                            window.h5PageTouchAreaTop || 0,
                            window.h5PageTouchAreaBottom || 0,
                            window.innerHeight,
                        );
                    },
                },
            });
        }
    };

    return (
        <div>
            <div className={styles.list}>
                {rowList.map((row, rowIndex) => (
                    <div className={styles.row} key={rowIndex}>
                        {row.map((col, colIndex) => {
                            const { formatUrl, isGif, isLong, isSingle, overSize, url } = col;

                            return (
                                <div className={cx('col', { colSingle: isSingle })} key={colIndex}>
                                    {isSingle && !isLong ? (
                                        <div
                                            className={styles.singleImgBox}
                                            onClick={e => {
                                                handleShowPic(e, colNum * rowIndex + colIndex);
                                            }}>
                                            <img src={formatImage(url, 600)} alt="" />
                                            {isGif ? <div className={styles.corner}>动图</div> : null}
                                        </div>
                                    ) : (
                                        <div
                                            className={styles.imgBox}
                                            onClick={e => {
                                                handleShowPic(e, colNum * rowIndex + colIndex);
                                            }}>
                                            <div className={styles.imgCard}>
                                                <LazyImg
                                                    className={styles.img}
                                                    placeholder={defaultImg}
                                                    src={formatUrl}
                                                />
                                            </div>
                                            {overSize && overSize > 0 ? (
                                                <div className={styles.oversize}>
                                                    <div>+{overSize}</div>
                                                </div>
                                            ) : null}
                                            {isGif ? <div className={styles.corner}>动图</div> : null}
                                            {isLong ? <div className={styles.corner}>长图</div> : null}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

Images.propTypes = {
    img: PropTypes.array,
    showType: PropTypes.string,
    id: PropTypes.string,
};

export default errorBoundary(Images);
